import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  adminApi,
  AccountRequisites,
  SaveAccountRequisitesRequest,
} from '../api/admin';
import { getErrorMessage } from '../api/client';
import { notifyError, notifySuccess } from '../utils/notify';
import { validateBankingIdentifiers } from '../utils/bankingValidation';

type PaymentMethod = 'SWIFT' | 'SEPA';

const EMPTY_FORM: SaveAccountRequisitesRequest = {
  paymentMethod: 'SWIFT',
  accountName: '',
  accountNumber: '',
  iban: '',
  swiftCode: '',
  bankName: '',
  bankLocation: '',
  bankAddress: '',
  branchCode: '',
  bankCode: '',
  intermediaryBankName: '',
  intermediaryBankSwift: '',
  isPrimary: true,
  notes: '',
};

function trim(value?: string | null): string {
  return value?.trim() || '';
}

function toPayload(form: SaveAccountRequisitesRequest, includeMethod: boolean): SaveAccountRequisitesRequest {
  const method = (form.paymentMethod || 'SWIFT').toUpperCase() as PaymentMethod;
  const payload: SaveAccountRequisitesRequest = {
    accountName: trim(form.accountName) || undefined,
    accountNumber: trim(form.accountNumber) || undefined,
    iban: trim(form.iban) || undefined,
    swiftCode: trim(form.swiftCode) || undefined,
    bankName: trim(form.bankName) || undefined,
    bankLocation: trim(form.bankLocation) || undefined,
    bankAddress: trim(form.bankAddress) || undefined,
    isPrimary: form.isPrimary ?? true,
    notes: trim(form.notes) || undefined,
  };

  if (includeMethod) payload.paymentMethod = method;
  if (method === 'SWIFT') {
    payload.branchCode = trim(form.branchCode) || undefined;
    payload.bankCode = trim(form.bankCode) || undefined;
    payload.intermediaryBankName = trim(form.intermediaryBankName) || undefined;
    payload.intermediaryBankSwift = trim(form.intermediaryBankSwift) || undefined;
  }

  return payload;
}

function paymentMethodOf(value?: string | null): PaymentMethod {
  return value?.trim().toUpperCase() === 'SEPA' ? 'SEPA' : 'SWIFT';
}

function isActiveStatus(value?: string | null): boolean {
  return value?.trim().toUpperCase() === 'ACTIVE';
}

function AccountRequisitesPanel({
  accountId,
  allowSepa = false,
}: {
  accountId: string;
  allowSepa?: boolean;
}) {
  const [items, setItems] = useState<AccountRequisites[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<SaveAccountRequisitesRequest>(EMPTY_FORM);
  const [deactivateId, setDeactivateId] = useState<string | null>(null);
  const [deactivateReason, setDeactivateReason] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await adminApi.getAccountRequisites(accountId, false);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load account requisites'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    void load();
    setFormOpen(false);
    setEditingId(null);
    setDeactivateId(null);
    setDeactivateReason('');
  }, [accountId, load]);

  const active = useMemo(
    () => items.filter((item) => isActiveStatus(item.status)),
    [items],
  );
  const hasSwift = active.some((item) => paymentMethodOf(item.paymentMethod) === 'SWIFT');
  const hasSepa = active.some((item) => paymentMethodOf(item.paymentMethod) === 'SEPA');
  const method = paymentMethodOf(form.paymentMethod);

  const openCreate = (paymentMethod: PaymentMethod) => {
    if (paymentMethod === 'SEPA' && !allowSepa) return;
    setEditingId(null);
    setForm({ ...EMPTY_FORM, paymentMethod, isPrimary: active.length === 0 });
    setFormOpen(true);
    setError('');
  };

  const openEdit = (item: AccountRequisites) => {
    setEditingId(item.id);
    setForm({
      paymentMethod: item.paymentMethod,
      accountName: item.accountName || '',
      accountNumber: item.accountNumber || '',
      iban: item.iban || '',
      swiftCode: item.swiftCode || '',
      bankName: item.bankName || '',
      bankLocation: item.bankLocation || '',
      bankAddress: item.bankAddress || '',
      branchCode: item.branchCode || '',
      bankCode: item.bankCode || '',
      intermediaryBankName: item.intermediaryBankName || '',
      intermediaryBankSwift: item.intermediaryBankSwift || '',
      isPrimary: item.isPrimary,
      notes: item.notes || '',
    });
    setFormOpen(true);
    setError('');
  };

  const save = async () => {
    const payload = toPayload(form, !editingId);
    if (method === 'SEPA') {
      if (!trim(payload.iban) || !trim(payload.swiftCode)) {
        setError('IBAN and BIC are required for SEPA requisites.');
        return;
      }
    } else {
      const identifierError = validateBankingIdentifiers(payload.accountNumber, payload.iban);
      if (identifierError) {
        setError(identifierError);
        return;
      }
    }

    try {
      setSaving(true);
      setError('');
      if (editingId) {
        await adminApi.updateAccountRequisites(editingId, payload);
        notifySuccess('Requisites updated.');
      } else {
        await adminApi.createAccountRequisites(accountId, payload);
        notifySuccess('Requisites created.');
      }
      setFormOpen(false);
      setEditingId(null);
      await load();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to save requisites'));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    if (!deactivateId || !trim(deactivateReason)) return;
    try {
      setSaving(true);
      setError('');
      await adminApi.deactivateAccountRequisites(deactivateId, trim(deactivateReason));
      notifySuccess('Requisites deactivated.');
      setDeactivateId(null);
      setDeactivateReason('');
      await load();
    } catch (err: unknown) {
      notifyError(err, 'Failed to deactivate requisites');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3">
        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Account Requisites</h4>
        <div className="flex gap-2">
          {!hasSwift && (
            <button
              type="button"
              onClick={() => openCreate('SWIFT')}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Add SWIFT
            </button>
          )}
          {allowSepa && !hasSepa && (
            <button
              type="button"
              onClick={() => openCreate('SEPA')}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            >
              Add SEPA
            </button>
          )}
        </div>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading requisites…</p>}
      {error && !formOpen && (
        <p className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      )}

      {!loading && items.length === 0 && !formOpen && (
        <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
          No payment requisites yet. Add SWIFT or SEPA details to issue account numbers and IBANs.
        </p>
      )}

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">{paymentMethodOf(item.paymentMethod)}</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  isActiveStatus(item.status) ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
                }`}>
                  {item.status}
                </span>
                {item.isPrimary && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Primary
                  </span>
                )}
              </div>
              {isActiveStatus(item.status) && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => openEdit(item)}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDeactivateId(item.id);
                      setDeactivateReason('');
                    }}
                    className="text-xs font-medium text-red-600 hover:text-red-800"
                  >
                    Deactivate
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              <RequisiteRow label="Account name" value={item.accountName} />
              <RequisiteRow label="Account number" value={item.accountNumber} mono />
              <RequisiteRow label="IBAN" value={item.iban} mono />
              <RequisiteRow label={paymentMethodOf(item.paymentMethod) === 'SEPA' ? 'BIC' : 'SWIFT'} value={item.swiftCode} mono />
              <RequisiteRow label="Bank name" value={item.bankName} />
              <RequisiteRow label="Bank location" value={item.bankLocation} />
              {paymentMethodOf(item.paymentMethod) === 'SWIFT' && (
                <>
                  <RequisiteRow label="Branch code" value={item.branchCode} />
                  <RequisiteRow label="Bank code" value={item.bankCode} />
                  <RequisiteRow label="Intermediary bank" value={item.intermediaryBankName} />
                  <RequisiteRow label="Intermediary SWIFT" value={item.intermediaryBankSwift} mono />
                </>
              )}
              <div className="sm:col-span-2">
                <RequisiteRow label="Bank address" value={item.bankAddress} />
              </div>
            </div>
            {deactivateId === item.id && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <label className="block text-xs font-medium text-gray-600 mb-1">Deactivation reason</label>
                <input
                  value={deactivateReason}
                  onChange={(e) => setDeactivateReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2"
                  placeholder="Required"
                />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setDeactivateId(null)} className="px-3 py-1.5 text-xs text-gray-600">
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={saving || !trim(deactivateReason)}
                    onClick={() => void deactivate()}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white disabled:opacity-50"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {formOpen && (
        <div className="mt-3 border border-indigo-100 bg-white rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-semibold text-gray-900">
              {editingId ? `Edit ${method} requisites` : `New ${method} requisites`}
            </h5>
            <button type="button" onClick={() => setFormOpen(false)} className="text-xs text-gray-500 hover:text-gray-700">
              Close
            </button>
          </div>
          {error && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Account name">
              <input value={form.accountName || ''} onChange={(e) => setForm((f) => ({ ...f, accountName: e.target.value }))} className={fieldClass} />
            </Field>
            {method === 'SWIFT' && (
              <Field label="Account number">
                <input value={form.accountNumber || ''} onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))} className={`${fieldClass} font-mono`} />
              </Field>
            )}
            <Field label="IBAN" required={method === 'SEPA'}>
              <input value={form.iban || ''} onChange={(e) => setForm((f) => ({ ...f, iban: e.target.value }))} className={`${fieldClass} font-mono`} />
            </Field>
            <Field label={method === 'SEPA' ? 'BIC' : 'SWIFT'} required={method === 'SEPA'}>
              <input value={form.swiftCode || ''} onChange={(e) => setForm((f) => ({ ...f, swiftCode: e.target.value }))} className={`${fieldClass} font-mono`} />
            </Field>
            <Field label="Bank name">
              <input value={form.bankName || ''} onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))} className={fieldClass} />
            </Field>
            <Field label="Bank location">
              <input value={form.bankLocation || ''} onChange={(e) => setForm((f) => ({ ...f, bankLocation: e.target.value }))} className={fieldClass} />
            </Field>
            {method === 'SWIFT' && (
              <>
                <Field label="Branch code">
                  <input value={form.branchCode || ''} onChange={(e) => setForm((f) => ({ ...f, branchCode: e.target.value }))} className={fieldClass} />
                </Field>
                <Field label="Bank code">
                  <input value={form.bankCode || ''} onChange={(e) => setForm((f) => ({ ...f, bankCode: e.target.value }))} className={fieldClass} />
                </Field>
                <Field label="Intermediary bank">
                  <input value={form.intermediaryBankName || ''} onChange={(e) => setForm((f) => ({ ...f, intermediaryBankName: e.target.value }))} className={fieldClass} />
                </Field>
                <Field label="Intermediary SWIFT">
                  <input value={form.intermediaryBankSwift || ''} onChange={(e) => setForm((f) => ({ ...f, intermediaryBankSwift: e.target.value }))} className={`${fieldClass} font-mono`} />
                </Field>
              </>
            )}
            <div className="sm:col-span-2">
              <Field label="Bank address">
                <input value={form.bankAddress || ''} onChange={(e) => setForm((f) => ({ ...f, bankAddress: e.target.value }))} className={fieldClass} />
              </Field>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={!!form.isPrimary}
              onChange={(e) => setForm((f) => ({ ...f, isPrimary: e.target.checked }))}
            />
            Primary requisites
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setFormOpen(false)} className="px-3 py-2 text-sm text-gray-600">
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => void save()}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const fieldClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </span>
      {children}
    </label>
  );
}

function RequisiteRow({
  label,
  value,
  mono,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
}) {
  const text = value?.trim();
  if (!text) return null;
  return (
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
      <p className={`text-sm text-gray-800 ${mono ? 'font-mono break-all' : ''}`}>{text}</p>
    </div>
  );
}

export default AccountRequisitesPanel;
